import { Check } from 'lucide-react';
import { STEP_ORDER, type TomboloStep } from '@/hooks/useTombolo';
import { cn } from '@/lib/utils';

const STEP_LABELS: Record<TomboloStep, string> = {
  notes: 'Notes',
  rules: 'Rules',
  participants: 'People',
  filter: 'Filter',
  tickets: 'Tickets',
  draw: 'Draw',
};

interface StepperProps {
  current: TomboloStep;
  /** Highest step the user is allowed to jump to */
  maxReached: TomboloStep;
  onNavigate: (step: TomboloStep) => void;
  isLocked: boolean;
}

export function Stepper({ current, maxReached, onNavigate, isLocked }: StepperProps) {
  const currentIdx = STEP_ORDER.indexOf(current);
  const maxIdx = STEP_ORDER.indexOf(maxReached);

  return (
    <nav aria-label="Raffle progress" className="w-full overflow-x-auto pb-1">
      <ol className="flex items-center gap-1 sm:gap-2 min-w-max mx-auto w-fit">
        {STEP_ORDER.map((step, i) => {
          const isCurrent = i === currentIdx;
          const isDone = i < currentIdx;
          // Once finalized, only tickets & draw are navigable
          const navigable = i <= maxIdx && (!isLocked || step === 'tickets' || step === 'draw');

          return (
            <li key={step} className="flex items-center gap-1 sm:gap-2">
              {i > 0 && (
                <div className={cn('h-px w-4 sm:w-8', isDone || isCurrent ? 'bg-primary' : 'bg-border')} aria-hidden />
              )}
              <button
                type="button"
                onClick={() => navigable && onNavigate(step)}
                disabled={!navigable}
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs sm:text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isCurrent && 'bg-primary text-primary-foreground',
                  isDone && !isCurrent && 'text-primary hover:bg-primary/10',
                  !isDone && !isCurrent && 'text-muted-foreground',
                  navigable && !isCurrent && 'cursor-pointer',
                  !navigable && 'cursor-default opacity-60',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full border text-[11px] tabular-nums shrink-0',
                    isCurrent && 'border-primary-foreground/50',
                    isDone && !isCurrent && 'border-primary bg-primary text-primary-foreground',
                    !isDone && !isCurrent && 'border-border',
                  )}
                  aria-hidden
                >
                  {isDone ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="hidden xs:inline sm:inline">{STEP_LABELS[step]}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
